import type { AcledEvent, MissileTrajectory, MissileType, NewsItem, SocialSignal, TooltipNewsItem } from '@/types';

// ---------------------------------------------------------------------------
// Missile Engine — 실시간 OSINT 기반 미사일 궤적
// 뉴스(RSS) + Telegram 시그널 + ACLED/LiveUAMap 이벤트에서 최근 24시간 내
// 미사일/타격 보도를 감지해 공격 corridor에 매핑. 감지된 이벤트가 없으면
// 아무것도 표시하지 않는다 (가짜 시뮬레이션 없음).
// ---------------------------------------------------------------------------

interface MissileScenario {
  id: string;
  type: MissileType;
  originLon: number;
  originLat: number;
  targetLon: number;
  targetLat: number;
  firedBy: string;
  targetName: string;
  flightTimeMs: number;
  cooldownMs: number;
  cycleOffsetMs: number;
  relatedNews: TooltipNewsItem[];
  /** 가장 최근 관련 보도 시각 (ms) */
  latestReportMs: number;
  /** 스웜 크기 — 보도된 수량 기반 ("400 drones" → 다중 arc), 기본 1 */
  swarm: number;
}

// 미사일/드론/타격 키워드 — 영어 + 키릴문자(Telegram OSINT). 이 중 하나라도 없으면 무시.
// 정치 기사 오탐 방지: 순수 동사(attack/strike 단독)는 제외, kinetic 명사만 포함.
const WEAPON_RE = /missile|ballistic|hypersonic|cruise|rocket|barrage|iskander|kinzhal|kalibr|shahed|geran|icbm|slbm|airstrike|air strike|air-strike|drone|uav|loitering|glide bomb|shelling|artillery|ракет|дрон|шахед|бпла|каб\b|обстріл|обстрел|авіаудар|авиаудар/i;

const BALLISTIC_RE  = /ballistic|icbm|slbm|iskander|іскандер|искандер/i;
const HYPERSONIC_RE = /hypersonic|kinzhal|fattah|zircon|кинжал|кинджал|циркон/i;
const DRONE_RE      = /drone|uav|shahed|geran|loitering|fpv|дрон|шахед|бпла|геран/i;

// 규모 파싱 — "400 drones", "80 ракет" → 스웜 렌더링
const COUNT_RE = /(\d{2,4})\+?\s*(?:drones?|missiles?|rockets?|дрон\w*|ракет\w*|шахед\w*|бпла)/i;

// 타격 대상 도시 좌표 (텍스트에서 도시명 감지 시 정밀 타겟팅)
const UA_CITIES: { re: RegExp; name: string; lon: number; lat: number }[] = [
  { re: /kyiv|kiev|киї|киев/i,      name: 'Kyiv',         lon: 30.52, lat: 50.45 },
  { re: /kharkiv|kharkov|харк/i,    name: 'Kharkiv',      lon: 36.23, lat: 49.99 },
  { re: /odesa|odessa|одес/i,       name: 'Odesa',        lon: 30.72, lat: 46.48 },
  { re: /lviv|львів|львов/i,        name: 'Lviv',         lon: 24.03, lat: 49.84 },
  { re: /dnipro|дніпр|днепр/i,      name: 'Dnipro',       lon: 35.05, lat: 48.46 },
  { re: /zaporizh|запоріж|запорож/i, name: 'Zaporizhzhia', lon: 35.14, lat: 47.84 },
];
const RU_CITIES: { re: RegExp; name: string; lon: number; lat: number }[] = [
  { re: /moscow|москв/i,                    name: 'Moscow',     lon: 37.62, lat: 55.75 },
  { re: /belgorod|бєлгород|белгород/i,      name: 'Belgorod',   lon: 36.58, lat: 50.60 },
  { re: /rostov|ростов/i,                   name: 'Rostov',     lon: 39.70, lat: 47.23 },
  { re: /crimea|sevastopol|крим|крым|севастопол/i, name: 'Crimea', lon: 33.52, lat: 44.60 },
  { re: /engels|saratov|енгельс|энгельс/i,  name: 'Engels AB',  lon: 46.12, lat: 51.48 },
];
const IL_TARGETS: { re: RegExp; name: string; lon: number; lat: number }[] = [
  { re: /gaza|rafah/i,               name: 'Gaza',     lon: 34.47, lat: 31.50 },
  { re: /lebanon|beirut|hezbollah/i, name: 'Lebanon',  lon: 35.50, lat: 33.90 },
  { re: /yemen|houthi|sanaa/i,       name: 'Yemen',    lon: 44.20, lat: 15.35 },
  { re: /iran|tehran/i,              name: 'Iran',     lon: 51.40, lat: 35.70 },
  { re: /syria|damascus/i,           name: 'Syria',    lon: 36.30, lat: 33.50 },
];

interface Corridor {
  id: string;
  /** 공격 주체 매칭 */
  attackerRe: RegExp;
  /** 대상 매칭 (없으면 attacker만으로 매칭) */
  targetRe?: RegExp;
  /**
   * 암시적 방향 추론 — 공격 주체 언급 없이 피격 도시만 보도되는 경우
   * (예: Telegram "Москву атакували дрони" → UA→RU). attackerRe 실패 시 이걸로 매칭.
   */
  implicitRe?: RegExp;
  origin: [number, number]; // [lon, lat]
  target: [number, number];
  firedBy: string;
  targetName: string;
  /** 도시 정밀 타겟팅 테이블 */
  cities?: { re: RegExp; name: string; lon: number; lat: number }[];
}

const CORRIDORS: Corridor[] = [
  {
    id: 'ru-ua',
    attackerRe: /russia|russian|росі|росси|рф\b/i,
    targetRe: /ukrain|kyiv|kiev|kharkiv|odesa|odessa|lviv|dnipro|zaporizh|україн|киї|харк|одес|дніпр|запоріж|львів/i,
    // 우크라이나 도시 피격 보도 (공격 주체 생략) → RU→UA로 추론
    implicitRe: /(киї|харк|одес|дніпр|запоріж|львів|україн|kyiv|kharkiv|odesa|dnipro|zaporizh)/i,
    origin: [37.0, 56.0], target: [30.52, 50.45],
    firedBy: 'Russia', targetName: 'Ukraine',
    cities: UA_CITIES,
  },
  {
    id: 'ua-ru',
    attackerRe: /ukrain|україн|зсу/i,
    targetRe: /moscow|belgorod|rostov|crimea|sevastopol|engels|saratov|refinery|russian (?:oil|air ?base|territory)|москв|бєлгород|белгород|ростов|крим|крым/i,
    // 러시아 도시 피격 보도 (공격 주체 생략) → UA→RU로 추론
    implicitRe: /(moscow|belgorod|москв|бєлгород|белгород|ростов|крим|крым|енгельс|энгельс)/i,
    origin: [31.2, 49.5], target: [37.62, 55.75],
    firedBy: 'Ukraine', targetName: 'Russia',
    cities: RU_CITIES,
  },
  {
    id: 'ye-rs',
    attackerRe: /houthi|ansar allah/i,
    origin: [44.2, 15.35], target: [42.5, 14.0],
    firedBy: 'Houthi (Yemen)', targetName: 'Red Sea shipping',
    cities: [
      { re: /israel|eilat|tel aviv/i, name: 'Israel', lon: 34.95, lat: 29.55 },
      { re: /ship|vessel|tanker|red sea/i, name: 'Red Sea shipping', lon: 42.5, lat: 14.0 },
    ],
  },
  {
    id: 'ir-il',
    attackerRe: /iran|irgc/i,
    targetRe: /israel|tel aviv/i,
    origin: [53.0, 32.5], target: [34.8, 31.8],
    firedBy: 'Iran (IRGC)', targetName: 'Israel',
  },
  {
    id: 'il-strike',
    attackerRe: /israel|idf/i,
    targetRe: /gaza|rafah|lebanon|beirut|hezbollah|yemen|houthi|iran|tehran|syria|damascus/i,
    origin: [34.8, 31.8], target: [34.47, 31.5],
    firedBy: 'Israel (IDF)', targetName: 'Gaza',
    cities: IL_TARGETS,
  },
  {
    id: 'kp-ea',
    attackerRe: /north korea|dprk|pyongyang/i,
    origin: [127.5, 39.3], target: [132.5, 40.2],
    firedBy: 'North Korea', targetName: 'East Sea / Sea of Japan',
  },
];

const FRESH_WINDOW_MS = 24 * 3600_000;  // 24시간 이내 보도만 사용
const HOT_WINDOW_MS   = 6 * 3600_000;   // 6시간 이내면 발사 빈도 증가
const MAX_SCENARIOS = 8;
const MAX_NEWS_PER_CORRIDOR = 3;

function detectType(text: string): MissileType {
  if (DRONE_RE.test(text)) return 'drone';
  if (HYPERSONIC_RE.test(text)) return 'hypersonic';
  if (BALLISTIC_RE.test(text)) return 'ballistic';
  return 'cruise';
}

// 보도 수량 → 스웜 크기 (12대당 arc 1개, 최대 36)
function detectSwarm(text: string, type: MissileType): number {
  const m = COUNT_RE.exec(text);
  if (m) return Math.max(1, Math.min(36, Math.round(parseInt(m[1], 10) / 12)));
  if (type === 'drone') return 8;                       // 드론 공격은 기본 다수
  if (/barrage|massive|масован|масштаб/i.test(text)) return 6;
  return 1;
}

const FLIGHT_MS: Record<MissileType, number> = {
  ballistic: 14_000,
  cruise: 16_000,
  hypersonic: 8_000,
  drone: 24_000, // 저속 순항
};

interface SourceItem {
  text: string;
  title: string;
  source: string;
  publishedAt: string;
  ms: number;
}

function collectSourceItems(
  news: NewsItem[],
  signals: SocialSignal[],
  acled: AcledEvent[],
  nowMs: number,
): SourceItem[] {
  const items: SourceItem[] = [];

  for (const n of news) {
    const ms = Date.parse(n.publishedAt);
    if (!Number.isFinite(ms) || nowMs - ms > FRESH_WINDOW_MS) continue;
    const text = `${n.title} ${n.description ?? ''}`;
    if (!WEAPON_RE.test(text)) continue;
    items.push({ text, title: n.title, source: n.source, publishedAt: n.publishedAt, ms });
  }

  for (const s of signals) {
    const ms = Date.parse(s.timestamp);
    if (!Number.isFinite(ms) || nowMs - ms > FRESH_WINDOW_MS) continue;
    if (!WEAPON_RE.test(s.text)) continue;
    items.push({ text: s.text, title: s.text.slice(0, 120), source: s.author, publishedAt: s.timestamp, ms });
  }

  for (const e of acled) {
    // event_date는 일 단위 → 최근 2일 이내만
    const ms = Date.parse(e.event_date);
    if (!Number.isFinite(ms) || nowMs - ms > 2 * 86400_000) continue;
    const text = `${e.notes} ${e.location} ${e.country}`;
    if (!WEAPON_RE.test(text)) continue;
    items.push({ text, title: e.notes.slice(0, 120) || `${e.event_type} — ${e.location}`, source: e.source || 'ACLED', publishedAt: e.event_date, ms });
  }

  return items.sort((a, b) => b.ms - a.ms);
}

/**
 * 실시간 데이터에서 미사일 시나리오 도출.
 * 감지된 이벤트가 없으면 빈 배열 → 지도에 미사일 없음.
 */
export function deriveLiveScenarios(
  news: NewsItem[],
  signals: SocialSignal[],
  acled: AcledEvent[],
  nowMs: number = Date.now(),
): MissileScenario[] {
  const items = collectSourceItems(news, signals, acled, nowMs);
  if (items.length === 0) return [];

  const byCorridor = new Map<string, SourceItem[]>();

  for (const item of items) {
    for (const c of CORRIDORS) {
      const explicit = c.attackerRe.test(item.text) && (!c.targetRe || c.targetRe.test(item.text));
      // 공격 주체 생략 보도 → 피격 도시로 방향 추론
      const implicit = !explicit && c.implicitRe ? c.implicitRe.test(item.text) : false;
      if (!explicit && !implicit) continue;
      const list = byCorridor.get(c.id) ?? [];
      if (list.length < MAX_NEWS_PER_CORRIDOR * 2) list.push(item);
      byCorridor.set(c.id, list);
      break; // 첫 매칭 corridor만
    }
  }

  const scenarios: MissileScenario[] = [];
  let idx = 0;

  for (const c of CORRIDORS) {
    const matched = byCorridor.get(c.id);
    if (!matched?.length) continue;

    const newest = matched[0];
    const type = detectType(newest.text);

    // 도시명 감지 시 정밀 타겟
    let target = c.target;
    let targetName = c.targetName;
    if (c.cities) {
      const city = c.cities.find((ct) => ct.re.test(newest.text));
      if (city) {
        target = [city.lon, city.lat];
        targetName = city.name;
      }
    }

    // 중복 헤드라인 제거 후 상위 3개
    const seen = new Set<string>();
    const relatedNews: TooltipNewsItem[] = [];
    for (const m of matched) {
      const key = m.title.toLowerCase().slice(0, 60);
      if (seen.has(key)) continue;
      seen.add(key);
      relatedNews.push({ title: m.title, source: m.source, publishedAt: m.publishedAt });
      if (relatedNews.length >= MAX_NEWS_PER_CORRIDOR) break;
    }

    const isHot = nowMs - newest.ms < HOT_WINDOW_MS;
    scenarios.push({
      id: `ms-live-${c.id}`,
      type,
      originLon: c.origin[0],
      originLat: c.origin[1],
      targetLon: target[0],
      targetLat: target[1],
      firedBy: c.firedBy,
      targetName,
      flightTimeMs: FLIGHT_MS[type],
      cooldownMs: isHot ? 8_000 : 25_000,
      cycleOffsetMs: idx * 4_000,
      relatedNews,
      latestReportMs: newest.ms,
      swarm: detectSwarm(matched.map((m) => m.text).join(' '), type),
    });
    idx++;
    if (scenarios.length >= MAX_SCENARIOS) break;
  }

  return scenarios;
}

// Interpolate position along great circle (simplified linear for short ranges)
function interpolatePosition(
  fromLon: number, fromLat: number,
  toLon: number, toLat: number,
  progress: number
): [number, number] {
  return [
    fromLon + (toLon - fromLon) * progress,
    fromLat + (toLat - fromLat) * progress,
  ];
}

// 결정적 지터 — 시나리오ID+인덱스 고정 시드 (리렌더 간 위치 불변)
function jitter(seed: string, k: number, scale: number): number {
  const s = `${seed}:${k}`;
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return ((Math.abs(h) % 1000) / 1000 - 0.5) * scale;
}

export function tickMissiles(scenarios: MissileScenario[], nowMs: number): MissileTrajectory[] {
  const out: MissileTrajectory[] = [];

  for (const scenario of scenarios) {
    const n = Math.max(1, scenario.swarm ?? 1);
    const cycleMs = scenario.flightTimeMs + scenario.cooldownMs;

    for (let k = 0; k < n; k++) {
      // 스웜: 발사 시점 스태거 + 좌표 지터 (k=0은 원본 경로)
      const oLon = scenario.originLon + (k > 0 ? jitter(scenario.id, k * 4 + 0, 1.6) : 0);
      const oLat = scenario.originLat + (k > 0 ? jitter(scenario.id, k * 4 + 1, 1.2) : 0);
      const tLon = scenario.targetLon + (k > 0 ? jitter(scenario.id, k * 4 + 2, 0.9) : 0);
      const tLat = scenario.targetLat + (k > 0 ? jitter(scenario.id, k * 4 + 3, 0.7) : 0);
      const elapsed = (nowMs + scenario.cycleOffsetMs + k * 1_700) % cycleMs;

      let status: MissileTrajectory['status'];
      let progress: number;
      let currentPos: [number, number];

      if (elapsed < scenario.flightTimeMs) {
        status = 'flying';
        progress = elapsed / scenario.flightTimeMs;
        currentPos = interpolatePosition(oLon, oLat, tLon, tLat, progress);
      } else if (elapsed < scenario.flightTimeMs + 2000) {
        status = 'impact';
        progress = 1.0;
        currentPos = [tLon, tLat];
      } else {
        status = 'cooldown';
        progress = 0;
        currentPos = [oLon, oLat];
      }

      out.push({
        id: n > 1 ? `${scenario.id}-${k}` : scenario.id,
        type: scenario.type,
        originLon: oLon,
        originLat: oLat,
        targetLon: tLon,
        targetLat: tLat,
        firedBy: scenario.firedBy,
        targetName: scenario.targetName,
        flightTimeMs: scenario.flightTimeMs,
        startedAt: nowMs - elapsed,
        status,
        impactAt: status === 'impact' ? nowMs : undefined,
        cooldownUntil: status === 'cooldown' ? nowMs + (cycleMs - elapsed) : undefined,
        relatedNews: scenario.relatedNews,
        progress,
        currentLon: currentPos[0],
        currentLat: currentPos[1],
      });
    }
  }

  return out;
}
